import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { Footer } from '@app/layout/footer/footer';
import { Navbar } from '@app/layout/navbar/navbar';

/**
 * Width presets for the centered content column. `default` matches the navbar
 * and footer so page content lines up with the shell.
 */
export type PageWidth = 'default' | 'narrow' | 'wide' | 'full';

/**
 * Shared shell for routed pages. Fills the space between the sticky navbar and
 * the footer so the footer sits at the bottom of the viewport even when the
 * page has little or no content.
 *
 * Usage:
 *   <app-page-container>
 *     <!-- page content -->
 *   </app-page-container>
 *
 *   <app-page-container width="narrow" [padded]="false"> ... </app-page-container>
 *
 *   <app-page-container [navbar]="false" [footer]="false"> ... </app-page-container>
 */
@Component({
  imports: [Navbar, Footer],
  selector: 'app-page-container',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrl: './page-container.css',
  templateUrl: './page-container.html',
})
export class PageContainer {
  /** Max width of the centered content column. */
  readonly width = input<PageWidth>('default');

  /** Toggle the default vertical padding around the content. */
  readonly padded = input(true);

  /** Show the shared navbar above the content. */
  readonly navbar = input(true);

  /** Render the navbar transparent/borderless until the page is scrolled. */
  readonly navbarOverlay = input(false);

  /** Show the shared footer below the content. */
  readonly footer = input(true);

  protected readonly maxWidthClass = computed(() => {
    switch (this.width()) {
      case 'narrow':
        return 'max-w-3xl';
      case 'wide':
        return 'max-w-screen-2xl';
      case 'full':
        return 'max-w-none';
      default:
        return 'max-w-8xl';
    }
  });
}
